// Phase 14e-a — pure utility for time-versioned supersedable entries
// (rate cards / rate modifiers / facility bill-rate overrides /
// cancellation policies / caregiver-guarantee policies). Shared by
// SupersedableResourcePanel + its create/supersede/deprecate modals.
//
// No React. No imports. Unit-testable in isolation; the spec runs
// before the component is built per the locked code-start order.

export type EntryStatus = 'active' | 'future-effective' | 'deprecated';

/**
 * 14-day lead-time mirror of `apps/backend/src/shared/lead-time/`'s
 * `LEAD_TIME_DAYS` constant. Kept in sync manually — both ends
 * enforce; backend is authoritative + client check is a UX guard.
 */
export const LEAD_TIME_DAYS = 14;

/**
 * Classify a supersedable entry into one of three display sections.
 *
 * Rules (in priority order):
 *   1. effectiveFrom > today → 'future-effective' (date comparison,
 *      reliable regardless of clock drift)
 *   2. isCurrent === true → 'active' (server-authoritative tie-breaker;
 *      handles the race where effectiveTo just passed but the server's
 *      next tick hasn't recomputed yet)
 *   3. Everything else → 'deprecated'
 *
 * The isCurrent gate prevents a visual flicker where an entry briefly
 * appears in the Deprecated section before the server confirms it's
 * still the active row.
 */
export function deriveEntryStatus(
  entry: { isCurrent: boolean; effectiveFrom: string; effectiveTo: string | null },
  now: Date,
): EntryStatus {
  const todayIso = toIsoDate(now);
  if (entry.effectiveFrom > todayIso) return 'future-effective';
  if (entry.isCurrent) return 'active';
  return 'deprecated';
}

/**
 * Returns true when the proposed effectiveFrom is within the
 * 14-day lead-time window AND no override has been requested.
 *
 * Used by every create / supersede / deprecate modal to disable
 * Submit + render the lead-time warning Alert. The backend ALSO
 * enforces (authoritative); this is a UX guard that saves a server
 * round-trip on invalid input.
 */
export function isLeadTimeViolation(
  effectiveFrom: string,
  now: Date,
  overrideLeadTime: boolean,
): boolean {
  if (overrideLeadTime) return false;
  const minDate = toIsoDate(addDays(now, LEAD_TIME_DAYS));
  return effectiveFrom < minDate;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * UTC midnight reference. All TrueCare time-versioned dates are
 * business-day dates (not clock times), so the UTC slice is the
 * right reference point — no DST drift, no timezone surprises.
 */
export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
